/**
 * product-marquee.js
 * Handles dynamic speed adjustment and seamless infinite-scroll
 * for the Product Marquee section.
 */

(function () {
  'use strict';

  class ProductMarquee {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.tracks = sectionEl.querySelectorAll('.product-marquee__track');
      this.speed = parseInt(sectionEl.dataset.speed, 10) || 30;
      this.pauseOnHover = sectionEl.dataset.pauseOnHover === 'true';

      this._init();
    }

    _init() {
      this.tracks.forEach((track) => {
        this._cloneListsIfNeeded(track);
        this._applySpeed(track);
      });

      if (this.pauseOnHover) {
        this._bindHoverPause();
      }

      // Re-check clone count on resize (viewport change may expose gaps)
      this._bindResize();
    }

    /**
     * Ensure the track is wide enough that the 50% translateX loop is gapless.
     * The section template already duplicates lists once via Liquid, but JS
     * adds a third copy if the viewport is very wide.
     */
    _cloneListsIfNeeded(track) {
      const lists = track.querySelectorAll('.product-marquee__list');
      if (!lists.length) return;

      const trackWidth = track.scrollWidth / lists.length;
      const viewportWidth = window.innerWidth;

      // Need at least 2× the viewport covered by each "half" of the track
      if (trackWidth < viewportWidth * 1.5) {
        const clone = lists[0].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }
    }

    /**
     * Adjust the CSS animation duration so that speed is consistent
     * regardless of the number of cards (it's expressed in seconds-per-set,
     * already set by Liquid, but JS can fine-tune based on actual pixel width).
     */
    _applySpeed(track) {
      const list = track.querySelector('.product-marquee__list');
      if (!list) return;

      // Speed = seconds to scroll one full list width (one "copy").
      // This keeps velocity constant at any card count.
      const listWidth = list.scrollWidth;
      const pixelsPerSecond = listWidth / this.speed;
      const duration = listWidth / pixelsPerSecond;

      track.style.animationDuration = `${duration}s`;
    }

    _bindHoverPause() {
      const wrappers = this.section.querySelectorAll('.product-marquee__track-wrapper');
      wrappers.forEach((wrapper) => {
        wrapper.addEventListener('mouseenter', () => {
          wrapper.querySelectorAll('.product-marquee__track').forEach((t) => {
            t.style.animationPlayState = 'paused';
          });
        });
        wrapper.addEventListener('mouseleave', () => {
          wrapper.querySelectorAll('.product-marquee__track').forEach((t) => {
            t.style.animationPlayState = 'running';
          });
        });
      });
    }

    _bindResize() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.tracks.forEach((track) => {
            this._cloneListsIfNeeded(track);
            this._applySpeed(track);
          });
        }, 250);
      });
    }
  }

  // ---------- Init ----------

  function initAll() {
    document.querySelectorAll('.product-marquee-section').forEach((el) => {
      if (!el.dataset.marqueeInit) {
        new ProductMarquee(el);
        el.dataset.marqueeInit = 'true';
      }
    });
  }

  // Standard DOM-ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor — re-init when section is reloaded
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.product-marquee-section');
    if (section) {
      delete section.dataset.marqueeInit;
      new ProductMarquee(section);
      section.dataset.marqueeInit = 'true';
    }
  });

})();
